import {
  AdjacencyTypes,
  GraphComponent,
  IEdge,
  IGraph,
  INode,
  IEnumerable,
} from '@ardoq/yfiles';
import type { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';
import { updateSelectedReferences } from 'streams/selection/actions';
import { edgeModelIds } from './edgeUtil';
import { dataModelId } from 'tabview/graphComponent/graphComponentUtil';
import { graphSelectionFinished } from 'tabview/graphComponent/graphEvents/actions';
import { componentGraphSelectionChanged } from '@ardoq/component-selection';
import { updateDetailsDrawer } from 'appLayout/ardoqStudio/detailsDrawer/actions';
import { hasContextAttributes, hasGraphItemMethods } from '@ardoq/graph';
import type { GraphItem } from '@ardoq/graph';
import type { GraphItem as OldGraphItem } from 'graph/GraphItem';
import { isTruthy } from '@ardoq/common-helpers';

export type GraphSelection = {
  graphComponent: GraphComponent;
  contextComponentId: ArdoqId;
  componentsById: Partial<Record<ArdoqId, APIComponentAttributes>>;
};

const onlyContextNodeIsSelected = (
  { graphComponent, contextComponentId, componentsById }: GraphSelection,
  selectedGraphItems: (GraphItem | OldGraphItem)[]
): boolean => {
  if (selectedGraphItems.length !== 1) {
    return false;
  }

  const maybeContextNode = selectedGraphItems[0];

  if (
    hasGraphItemMethods(maybeContextNode) &&
    hasContextAttributes(maybeContextNode)
  ) {
    return Boolean(
      maybeContextNode.isComponent() && maybeContextNode.getIsContext()
    );
  }

  return (
    Boolean(componentsById[contextComponentId]) &&
    dataModelId(maybeContextNode) === contextComponentId &&
    graphComponent.selection.edges.size === 0
  );
};

export const resetNodesAndEdgesConnectedStyles = (graph: IGraph) => {
  graph.nodes.forEach(node => {
    if (node.tag) {
      node.tag.isTransparentized = false;
    }
  });
  graph.edges.forEach(edge => {
    edge.tag.isTransparentized = false;
  });
};

const markUnconnectedNodesAndEdges = (
  graph: IGraph,
  connectedNodes: Set<INode>,
  connectedEdges: Set<IEdge>
) => {
  graph.nodes.forEach(node => {
    if (!connectedNodes.has(node)) {
      if (node.tag) {
        node.tag.isTransparentized = true;
      }
    }
  });
  graph.edges.forEach(edge => {
    if (!connectedEdges.has(edge)) {
      edge.tag.isTransparentized = true;
    }
  });
};

export const getConnectedNodesAndEdges = (
  graph: IGraph,
  selectedNodes: IEnumerable<INode> | INode[],
  selectedEdges: IEnumerable<IEdge> | IEdge[]
) => {
  const connectedNodes = new Set<INode>();
  const connectedEdges = new Set<IEdge>();

  selectedNodes.forEach(currentNode => {
    connectedNodes.add(currentNode);

    // Add outgoing edges and their target nodes
    graph.edgesAt(currentNode, AdjacencyTypes.OUTGOING).forEach(edge => {
      const targetNode = edge.targetNode;
      connectedEdges.add(edge);
      if (targetNode) {
        connectedNodes.add(targetNode);
      }
    });

    // Add incoming edges and their source nodes
    graph.edgesAt(currentNode, AdjacencyTypes.INCOMING).forEach(edge => {
      const sourceNode = edge.sourceNode;
      connectedEdges.add(edge);
      if (sourceNode) {
        connectedNodes.add(sourceNode);
      }
    });
  });

  selectedEdges.forEach(edge => {
    const sourceNode = edge.sourceNode;
    const targetNode = edge.targetNode;
    if (sourceNode) {
      connectedNodes.add(sourceNode);
    }
    if (targetNode) {
      connectedNodes.add(targetNode);
    }
    connectedEdges.add(edge);
  });

  return { connectedNodes, connectedEdges };
};

export const updateNodesAndEdgesConnectednessToSelection = (
  graphComponent: GraphComponent
) => {
  const selectedNodes = graphComponent.selection.nodes;
  const selectedEdges = graphComponent.selection.edges;
  const graph = graphComponent.graph;

  resetNodesAndEdgesConnectedStyles(graph);

  if (!(selectedNodes.size || selectedEdges.size)) {
    graphComponent.invalidate();
    return;
  }

  const { connectedNodes, connectedEdges } = getConnectedNodesAndEdges(
    graph,
    selectedNodes,
    selectedEdges
  );

  markUnconnectedNodesAndEdges(graph, connectedNodes, connectedEdges);

  graphComponent.invalidate();
};

type SelectionData = {
  selectedGraphItems: (GraphItem | OldGraphItem)[];
  selectedNodeModelIds: string[];
  selectedReferenceIds: string[];
};

const getSelectionData = (graphComponent: GraphComponent): SelectionData => {
  const selectedGraphItems = graphComponent.selection.nodes
    .map(node => node.tag)
    .toArray();
  const selectedNodeModelIds = selectedGraphItems
    .map(dataModelId)
    .filter(isTruthy);
  const selectedReferenceIds = graphComponent.selection.edges
    .toArray()
    .flatMap(edgeModelIds);
  return { selectedGraphItems, selectedNodeModelIds, selectedReferenceIds };
};

const updateGraphSelection = (
  selectedNodeIds: string[],
  selectedReferenceIds: string[]
) => {
  dispatchAction(
    updateSelectedReferences({ referenceIds: selectedReferenceIds })
  );
  dispatchAction(
    componentGraphSelectionChanged({ graphSelection: selectedNodeIds })
  );
};

export const handleSelectionChanged = (
  graphComponent: GraphComponent
): void => {
  dispatchAction(graphSelectionFinished({ graphComponent }));
};

export const respondToGraphSelection = (selection: GraphSelection): void => {
  const { graphComponent } = selection;
  const { selectedGraphItems, selectedNodeModelIds, selectedReferenceIds } =
    getSelectionData(graphComponent);

  updateGraphSelection(selectedNodeModelIds, selectedReferenceIds);
  if (onlyContextNodeIsSelected(selection, selectedGraphItems)) {
    graphComponent.selection.clear();
    return;
  }

  updateNodesAndEdgesConnectednessToSelection(graphComponent);
};

export const handleSelectionChangedModernizedBlockDiagram = (
  graphComponent: GraphComponent
) => {
  const { selectedNodeModelIds, selectedReferenceIds } =
    getSelectionData(graphComponent);

  updateGraphSelection(selectedNodeModelIds, selectedReferenceIds);

  if (selectedNodeModelIds.length === 1) {
    dispatchAction(updateDetailsDrawer(selectedNodeModelIds));
  }
  updateNodesAndEdgesConnectednessToSelection(graphComponent);
};
